import { useCallback, useMemo, useEffect, useRef } from "react";
import { useLLMSelectionStore } from "../store/llm-selection-store";
import { useToast } from "@/features/toast";
import { useLLMModels } from "@/api/hooks";
import type { LLMModel } from "@/api/models/api-types";

export const useLLMSelection = () => {
  const {
    selectedModelName,
    selectedModel: selectedModelFromStore,
    selectModel: selectModelAction,
    clearSelection,
  } = useLLMSelectionStore();

  const { info } = useToast();

  // API'den modelleri fetch et (infinite) - Always enabled for model selection
  const {
    data: modelsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLLMModels({
    enabled: true, // ✅ Always fetch models for selection
  });

  // 🎯 Model bulma denemesi için ref (sonsuz döngüyü önlemek için)
  const fetchAttemptRef = useRef(0);
  const maxFetchAttempts = 3; // Maksimum 3 kez deneme

  // 🎯 Model Selection Logic - Önce store'dan, sonra API'den ara
  const selectedModel = useMemo(() => {
    // ✅ Önce store'dan al (en hızlı ve güvenilir)
    if (selectedModelFromStore) {
      return selectedModelFromStore;
    }

    // ✅ Fallback: API'den gelen modeller arasında ara
    if (!modelsData?.pages || !selectedModelName) return null;
    const allModels = modelsData.pages.flatMap((p) => p.data.result);
    const availableModels = allModels.filter((m) => !m.isDeleted);
    const foundModel = availableModels.find(
      (model) => model.name === selectedModelName,
    );
    return foundModel || null;
  }, [selectedModelFromStore, modelsData, selectedModelName]);

  // 🎯 selectedModelName değiştiğinde ref'i sıfırla
  useEffect(() => {
    fetchAttemptRef.current = 0;
  }, [selectedModelName]);

  // 🎯 Cache'den yükleme sonrası model bulma - API'den modeller geldiğinde store'a kaydet
  useEffect(() => {
    // Eğer selectedModelName varsa ama selectedModel yoksa ve API'den modeller geldiyse
    if (
      selectedModelName &&
      !selectedModelFromStore &&
      modelsData?.pages &&
      !isLoading
    ) {
      const allModels = modelsData.pages.flatMap((p) => p.data.result);
      const availableModels = allModels.filter((m) => !m.isDeleted);
      const foundModel = availableModels.find(
        (model) => model.name === selectedModelName,
      );

      // ✅ Model bulunduysa store'a kaydet
      if (foundModel) {
        useLLMSelectionStore.setState({
          selectedModel: foundModel,
        });
      } else if (
        hasNextPage &&
        !isFetchingNextPage &&
        fetchAttemptRef.current < maxFetchAttempts
      ) {
        // ✅ Model bulunamadıysa ve daha fazla sayfa varsa, sonraki sayfayı getir (maksimum 3 kez)
        fetchAttemptRef.current += 1;
        fetchNextPage();
      }
    }
  }, [
    selectedModelName,
    selectedModelFromStore,
    modelsData,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // 🎯 Auto-select logic removed - handled by useAutoSelectFirstModel hook

  const selectModel = useCallback(
    (model: LLMModel) => {
      selectModelAction(model);
      info(
        `Dil modeli değiştirildi: <span style='font-weight:600'>${
          model.title || model.name
        }</span>`,
        { title: "Bilgi" },
      );
    },
    [selectModelAction, info],
  );

  return {
    // State - Simple and clean
    selectedModel, // ✅ Computed model object
    selectedModelName, // ✅ Persisted model name
    isModelLoaded: !!selectedModel, // ✅ Model loading status
    isLoading, // ✅ API loading status

    // Actions
    selectModel,
    clearSelection,
  };
};
